import { FC, useEffect, useRef, useState } from "react";
import { FaTrashAlt, FaPlus } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

interface Image {
  path: string;
  public_id: string;
  primaryImg: boolean;
  _id: string;
}

interface ImageUploadProps {
  onUpload: (images: (File | Image)[]) => void; // Updated type to handle both File and existing Image
}

const ImageUpload: FC<ImageUploadProps> = ({ onUpload }) => {
  const { productDetails } = useSelector(
    (state: any) => state.rootReducer?.productDetail
  );

  const [existingImages, setExistingImages] = useState<Image[]>([]);
  const [newImages, setNewImages] = useState<{ file: File; preview: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const params = useParams();

  useEffect(() => {
    // Load existing images from productDetails
    if (productDetails?.images) {
      setExistingImages(productDetails.images);
    }
  }, [productDetails]);

  const updateCombinedImages = () => {
    console.log('existingImages', existingImages)
    // Combine existing images and new image files
    const combinedImages = [
      ...existingImages, // Existing images
      ...newImages.map((img) => img.file), // New image files
    ];
    onUpload(combinedImages);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files).map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));

      setNewImages((prev) => [...prev, ...fileArray]);
    }
  };

  const handleRemoveExistingImage = (publicId: string) => {
    const updatedImages = existingImages.filter((img) => img.public_id !== publicId);
    setExistingImages(updatedImages);
  };

  const handleRemoveNewImage = (index: number) => {
    const updatedImages = newImages.filter((_, i) => i !== index);
    setNewImages(updatedImages);
  };

  useEffect(() => {
    // Update combined images whenever newImages or existingImages changes
    updateCombinedImages();
  }, [existingImages, newImages]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        JPEG / PNG, Max Size - 5MB
      </label>
      <div className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
        <span>{existingImages.length + newImages.length} Images</span>
      </div>
      <div className="grid grid-cols-8 gap-4">
        {/* Render existing images */}
        {existingImages.map((img) => (
          <div key={img.public_id} className="relative w-32 h-32">
            <img
              src={img.path}
              alt="Existing"
              className="w-full h-full object-cover rounded-md"
            />
            <button
              onClick={() => handleRemoveExistingImage(img.public_id)}
              className="absolute top-1 right-1 bg-primary1 text-white rounded-full p-2 hover:bg-primary3 shadow-md"
            >
              <FaTrashAlt />
            </button>
          </div>
        ))}

        {/* Render new images */}
        {newImages.map((img, index) => (
          <div key={index} className="relative w-32 h-32">
            <img
              src={img.preview}
              alt={`Uploaded ${index + 1}`}
              className="w-full h-full object-cover rounded-md"
            />
            <button
              onClick={() => handleRemoveNewImage(index)}
              className="absolute top-1 right-1 bg-primary1 text-white rounded-full p-2 hover:bg-primary3 shadow-md"
            >
              <FaTrashAlt />
            </button>
          </div>
        ))}

        {/* Add new image button */}
        <div
          onClick={handleUploadClick}
          className="w-32 h-32 flex items-center justify-center border border-dashed border-gray-300 rounded-md cursor-pointer hover:border-primary3 transition-all"
        >
          <FaPlus className="text-primary1 text-3xl" />
        </div>
      </div>

      <input
        type="file"
        accept="image/*"
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

export default ImageUpload;
